import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const RichTextEditor = ({ value, onChange, placeholder, placeholders = [] }) => {
  const quillRef = useRef(null);

  // Custom toolbar configuration
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'script',
    'direction',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  // Insert placeholder at cursor position
  const insertPlaceholder = (placeholder) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      const position = range ? range.index : quill.getLength() - 1;
      
      // Insert the placeholder text
      quill.insertText(position, `{{${placeholder}}}`, {
        'color': '#059669',
        'bold': true
      });
      
      // Move cursor after the inserted placeholder
      quill.setSelection(position + placeholder.length + 4);
    }
  };

  // Get placeholder description for tooltip
  const getPlaceholderDescription = (placeholder) => {
    const descriptions = {
      'name': 'User\'s full name',
      'username': 'User\'s username',
      'email': 'User\'s email address',
      'company': 'Company name',
      'company_name': 'Company name',
      'dataroom_name': 'Name of the dataroom',
      'admin_name': 'Administrator\'s name',
      'admin_email': 'Administrator\'s email',
      'created_at': 'Creation date',
      'approved_at': 'Approval date',
      'pricing_plan_name': 'Pricing plan name',
      'otp': 'One-time password',
      'subscription_id': 'Subscription ID',
      'first_name': 'User\'s first name',
      'last_name': 'User\'s last name',
      'phone': 'User\'s phone number',
      'role': 'User\'s role',
      'status': 'Status information',
      'date': 'Date information',
      'time': 'Time information',
      'url': 'URL link',
      'link': 'Link URL',
      'password': 'Password information',
      'token': 'Authentication token'
    };
    return descriptions[placeholder.toLowerCase()] || `Insert ${placeholder}`;
  };

  return (
    <div className="rich-text-editor-container">
      <style>{`
        .rich-text-editor-container .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          border-color: #d1d5db;
          background-color: #f9fafb;
        }
        
        .rich-text-editor-container .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
          border-color: #d1d5db;
          font-family: inherit;
          font-size: 0.875rem;
          min-height: 200px;
        }
        
        .rich-text-editor-container .ql-editor {
          min-height: 200px;
        }
        
        .rich-text-editor-container .ql-editor.ql-blank::before {
          font-style: normal;
          color: #9ca3af;
        }
        
        .rich-text-editor-container .ql-snow .ql-tooltip {
          z-index: 9999;
        }
        
        .placeholder-button {
          transition: all 0.2s ease;
          position: relative;
        }
        
        .placeholder-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          background-color: #f0f9ff !important;
          border-color: #0ea5e9 !important;
          color: #0c4a6e !important;
        }
        
        /* Enhanced tooltip styling */
        .placeholder-button[title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: #111827;
          color: #ffffff;
          padding: 8px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          font-family: system-ui, -apple-system, sans-serif;
          white-space: nowrap;
          z-index: 9999;
          opacity: 0;
          animation: tooltipFadeIn 0.2s ease-in-out forwards;
          margin-bottom: 6px;
          pointer-events: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }
        
        .placeholder-button[title]:hover::before {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #111827;
          z-index: 9999;
          opacity: 0;
          animation: tooltipFadeIn 0.2s ease-in-out forwards;
          pointer-events: none;
          margin-bottom: 1px;
        }
        
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
      
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      
      {/* Placeholder Buttons */}
      {placeholders.length > 0 && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-xs font-medium text-gray-600 mb-2">
            Click to insert placeholder:
          </p>
          <div className="flex flex-wrap gap-2">
            {placeholders.map((placeholder) => (
              <button
                key={placeholder}
                type="button"
                onClick={() => insertPlaceholder(placeholder)}
                title={getPlaceholderDescription(placeholder)}
                className="placeholder-button px-3 py-1.5 text-xs font-mono bg-white border border-gray-300 rounded-md hover:bg-primary-50 hover:border-primary-300 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
              >
                {`{{${placeholder}}}`}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;